import React from 'react';

import { cssClass } from '../../style';

export interface FileDragAndDropProps {
  handleDrop: (event: React.ChangeEvent<HTMLInputElement>) => void;
  testId: string;
  containerClassName: string;
  defaultStateClassName: string;  // The state with no object
  onDragClassName: string;        // State when object enters the drop area
  activeStateClassName: string;   // The state with an object dropped
}

export interface FileDragAndDropState {
  isDragOver: boolean;
  hasDropped: boolean;
}

// Prevent unnecessary onDragLeave events triggering re-renders
const ignoreChildPointerEvents = cssClass('file-drag-and-drop', 'container', {
  $nest: {
    '& *': {
      pointerEvents: 'none'
    }
  }
});

export class FileDragAndDrop extends React.PureComponent<FileDragAndDropProps, FileDragAndDropState> {

  private originalDocumentDragOverHandler: any;
  private originalDocumentDragEnterHandler: any;
  private originalDocumentDropHandler: any;

  constructor(props: FileDragAndDropProps) {
    super(props);
    this.state = {
      isDragOver: false,
      hasDropped: false
    };
  }

  private preventDropOnDocument = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
  };

  public componentDidMount(){
    // make a copy of existing document event handlers
    this.originalDocumentDragOverHandler = document.ondragover;
    this.originalDocumentDragEnterHandler = document.ondragenter;
    this.originalDocumentDropHandler = document.ondrop;

    // disable drag event handing on document
    document.ondragover = document.ondragenter = document.ondrop = this.preventDropOnDocument;
  }

  public componentWillUnmount(){
    // restore event handing on document
    document.ondragover = this.originalDocumentDragOverHandler;
    document.ondragenter = this.originalDocumentDragEnterHandler;
    document.ondrop = this.originalDocumentDropHandler;
  }

  private handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    this.setState({
      isDragOver: true
    });
  };

  private handleDragLeave = () => {
    this.setState({
      isDragOver: false
    });
  };

  private handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      const customEvent = {
        target: {
          files: [
            event.dataTransfer.files[0]
          ]
        }
      };
      this.props.handleDrop(customEvent as unknown as React.ChangeEvent<HTMLInputElement>);
      this.setState({
        isDragOver: false,
        hasDropped: true
      });
    }
  };

  private getContainerClass = () => {
    let classNameArray = [this.props.containerClassName];

    if (this.state.hasDropped) {
      classNameArray.push(this.props.activeStateClassName);
    } else {
      classNameArray.push(this.props.defaultStateClassName);
    }

    if (this.state.isDragOver) {
      classNameArray.push(this.props.onDragClassName);
      classNameArray.push(ignoreChildPointerEvents);
    }

    return classNameArray.join(' ');
  };

  render() {
    return (
      <div
        className={this.getContainerClass()}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
        data-tb-test-id={`${this.props.testId}-drag-and-drop`}
      >
        {this.props.children}
      </div>
    );
  }
}

export function preventDefaultDrag(event: React.DragEvent<HTMLElement>) {
  event.preventDefault();
  event.stopPropagation();
}
